import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Input, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule } from '@ngx-translate/core';
import { MaterialModule } from '../../../../../shared/modules/material/material.module';
import { MatSelect } from '@angular/material/select';
import { FiscalData } from '../../../../../core/models/user.models';
import { UserService } from '../../../../../core/services/user/user.service';
import { CountryService } from '../../../../../core/services/country/country.service';
import { Country, CountryDivision, Destination } from '../../../../../core/models/country.models';
import { concatMap, of, switchMap } from 'rxjs';

@Component({
  selector: 'app-fiscal-data-submenu',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MaterialModule, FontAwesomeModule, TranslateModule],
  templateUrl: './fiscal-data-submenu.component.html',
  styleUrl: './fiscal-data-submenu.component.scss'
})
export class FiscalDataSubmenuComponent {
  @Input() fiscalData!: FiscalData | null;

  @ViewChild("documentTypeSelected", { read: MatSelect, static: false }) documentSelect!: MatSelect;
  @ViewChild("countrySelected", { read: MatSelect, static: false }) countrySelect!: MatSelect;
  @ViewChild("countryDivisionSelected", { read: MatSelect, static: false }) countryDivisionSelect!: MatSelect;
  @ViewChild("countryDestinationSelected", { read: MatSelect, static: false }) countryDestinationSelect!: MatSelect;

  countries: Country[] = [];
  countryDivision: CountryDivision[] = [];
  countryDestinations: Destination[] = [];
  documentTypes: string[] = [];

  formGroup!: FormGroup;

  constructor(private userService: UserService, private countryService: CountryService, private cdr: ChangeDetectorRef) {

    this.formGroup = new FormGroup(
      {
        documentType: new FormControl<string[]>([], [Validators.required]),
        documentNumber: new FormControl<string>("", [Validators.required, Validators.pattern("^[0-9]+$")]),
        fiscalName: new FormControl<string>("", [Validators.required]),
        address: new FormControl<string>(""),
        country: new FormControl<string[]>([], [Validators.required]),
        countryDivition: new FormControl<string[]>([]),
        destination: new FormControl<string[]>([]),
      }
    )
  }

  ngOnInit() {
    this.userService.getAllDocumentTypes().subscribe((documents: string[]) => {
      this.documentTypes = documents;
    });
    this.countryService.getAllCountries().subscribe((countries: Country[]) => {
      this.countries = countries;
    })
  }

  ngAfterViewInit() {
    if (this.fiscalData) {
      const fiscalDocumentNumber = this.fiscalData?.documentNumber ?? "";
      const fiscalAddress = this.fiscalData?.address ?? "";
      const country = this.fiscalData.countryId ? [this.fiscalData.countryId] : [];
      const countryDivition = this.fiscalData.countryDivisionId ? [this.fiscalData.countryDivisionId] : [];
      const destination = this.fiscalData.destinationId ? [Number(this.fiscalData.destinationId)] : [];

      //MANDATORY
      this.formGroup.get("documentType")?.setValue([this.fiscalData?.documentType]);
      this.formGroup.get("fiscalName")?.setValue(this.fiscalData?.fiscalName);
      this.formGroup.get("country")?.setValue(country);
      this.formGroup.get("documentNumber")?.setValue(fiscalDocumentNumber);

      //OPTIONAL
      this.formGroup.get("address")?.setValue(fiscalAddress);
      this.formGroup.get("countryDivition")?.setValue(countryDivition);
      this.formGroup.get("destination")?.setValue(destination);

      if (country.length) {
        this.loadCountryDivisionAndDestination(country[0]);
      }

      this.cdr.detectChanges();
    }
  }


  clearInput(key: string) {
    this.formGroup.get(key)?.reset();
  }

  updateChkbxArray(isChecked: boolean, id: string, key: string) {
    const formControl = this.formGroup.get(key);
    isChecked ? formControl?.setValue([id]) : formControl?.setValue([]);

    switch (key) {
      case "country": {
        const countryId = formControl?.value.length ? formControl.value[0] : null;
        this.formGroup.get('countryDivition')?.setValue([]);
        this.formGroup.get('destination')?.setValue([]);
        this.loadCountryDivisions(countryId);
        break;
      }
      case "countryDivition": {
        const countryDivisionId = formControl?.value.length ? formControl.value[0] : null;
        this.formGroup.get('destination')?.setValue([]);
        this.loadDestinations(countryDivisionId);
        break;
      }
    }
  }

  //This function is created because otherwise when you click on the mat-option text (not the mat-checkbox) 
  //the updateChkbxArray functionality is not triggered.
  propagateCheck(recordId: string, formControlName: string) {
    const currentState = this.isChecked(recordId, formControlName);
    this.updateChkbxArray(currentState, recordId, formControlName);
  }

  isChecked(recordId: string, controlName: string): boolean {
    const formControl = this.formGroup.get(controlName)?.value;
    return formControl.includes(recordId);
  }

  loadCountryDivisionAndDestination(countryId: string) {
    this.countryDivision = [];
    this.countryDestinations = [];
    if (countryId) {
      this.countryService.getCountryDivision(countryId).pipe(
        concatMap((countryDivision: CountryDivision[]) => {
          this.countryDivision = countryDivision;
          if (this.countryDivision.length) {
            this.countryDestinations = [];
          }
          
          const countryDivition = this.fiscalData?.countryDivisionId ? [this.fiscalData.countryDivisionId] : [];
          if(countryDivition.length) {
            return this.countryService.getDestination(countryDivition[0]);
          } else {
            return of([]);
          }
        })
      ).subscribe((destinations: Destination[]) => {
        this.countryDestinations = destinations;
      })
    }
  }

  loadCountryDivisions(countryId: string) {
    this.countryDivision = [];
    this.countryDestinations = [];

    if (countryId) {
      this.countryService.getCountryDivision(countryId).subscribe((countryDivisions: CountryDivision[]) => {
        this.countryDivision = countryDivisions;
      })
    }

  }

  loadDestinations(countryDivisionId: string) {
    this.countryDestinations = [];

    if (countryDivisionId) {
      this.countryService.getDestination(countryDivisionId).subscribe((destinations: Destination[]) => {
        this.countryDestinations = destinations;
      })
    }
  }
}
